/**
 * The day's trips as a list, the Main board's "Routes" column: each trip by
 * name, with its load and its farms, and each a link that selects it.
 *
 * The same order as the timeline (by vehicle, then departure), so a keyboard
 * meets the trips in the same sequence in both places.
 */
import { Section } from "@depicomnlbv/platform-ui";
import { Link } from "react-router-dom";

import type { Day } from "../api";
import { formatClock, formatLitres } from "../time";
import { tripLabels, tripsInOrder, vehicleName } from "../trips";

export function TripList({ day, selected }: { day: Day; selected: number | null }) {
  const labels = tripLabels(day.trips);
  const trips = tripsInOrder(day.trips);

  return (
    <Section empty={trips.length === 0 ? "No trips are planned on this day." : undefined} title="Trips">
      {trips.length === 0 ? null : (
        <ul aria-label="Trips on this day" className="trip-list" role="list">
          {trips.map((trip) => {
            const isSelected = trip.route_id === selected;
            const farms = trip.stops.length === 1 ? "1 farm" : `${trip.stops.length} farms`;
            const times =
              trip.departure && trip.return
                ? `${formatClock(trip.departure, day.time_zone, day.date)} to ${formatClock(trip.return, day.time_zone, day.date)}`
                : "times not recorded";
            return (
              <li key={trip.route_id}>
                <Link
                  aria-current={isSelected ? "true" : undefined}
                  className={isSelected ? "trip-list__item trip-list__item--selected" : "trip-list__item"}
                  to={{ search: `?trip=${trip.route_id}` }}
                >
                  <span className="trip-list__name">
                    {labels.get(trip.route_id)} · {vehicleName(trip.vehicle)}
                  </span>{" "}
                  <span className="trip-list__load tabular">
                    {trip.litres === null ? "load not recorded" : formatLitres(trip.litres)}
                  </span>{" "}
                  <span className="trip-list__meta">
                    {farms} · {times}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
